import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Phone, X } from "lucide-react";
import { BRAND } from "@/content/site";
import { GALLERY_GROUPS, IMAGES } from "@/content/media";
import { useLang } from "@/lang";
import BrandsPanel from "@/components/BrandsPanel";

const EASE = [0.22, 1, 0.36, 1];

export default function Gallery() {
  const { t, isEs } = useLang();
  const lang = isEs ? "es" : "en";
  const [group, setGroup] = useState("all");
  const [open, setOpen] = useState(-1);

  const shown = group === "all" ? IMAGES.photos : IMAGES.photos.filter((p) => p.group === group);
  const pick = (k) => {
    setGroup(k);
    setOpen(-1);
  };
  const step = (d) => setOpen((i) => (i + d + shown.length) % shown.length);

  useEffect(() => {
    if (open < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(-1);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    const onClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-testid="lightbox-close"]')) return setOpen(-1);
      if (t.closest('[data-testid="lightbox-next"]')) return step(1);
      if (t.closest('[data-testid="lightbox-prev"]')) return step(-1);
      if (t.closest('[data-testid="lightbox-figure"]')) return;
      if (t.closest('[data-testid="lightbox"]')) setOpen(-1);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick, true);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onClick, true);
      document.body.style.overflow = "";
    };
  }, [open, shown.length]);

  return (
    <section id="equipment" data-testid="equipment-gallery-section" className="py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8, ease: EASE }} className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.gallery.eyebrow}</p>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }} className="font-serif text-4xl font-medium leading-[1.1] text-navy sm:text-5xl">
            {t.gallery.title}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8, ease: EASE, delay: 0.2 }} className="mt-5 text-base leading-relaxed text-mutedink sm:text-lg">
            {t.gallery.sub}
          </motion.p>
        </div>

        <div className="mt-10 flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible" role="tablist" aria-label={t.gallery.eyebrow}>
          {GALLERY_GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              role="tab"
              aria-selected={group === g.key}
              data-testid={`gallery-filter-${g.key}`}
              onClick={() => pick(g.key)}
              className={`shrink-0 rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                group === g.key
                  ? "border-gold bg-gold text-navy"
                  : "border-hairline bg-white text-navy/70 hover:border-gold/60 hover:text-navy"
              }`}
            >
              {g.label[lang]}
            </button>
          ))}
        </div>

        <div className="mt-8 grid auto-rows-[230px] grid-cols-2 gap-3 sm:auto-rows-[270px] sm:gap-4 lg:grid-cols-4">
          {shown.map((p, i) => (
            <motion.figure
              key={p.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: EASE, delay: Math.min(i, 7) * 0.05 }}
              data-testid={`gallery-tile-${p.key}`}
              role="button"
              tabIndex={0}
              aria-label={p.alt[lang]}
              onClick={() => setOpen(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setOpen(i);
              }}
              className="group relative cursor-zoom-in overflow-hidden rounded-2xl bg-navy"
            >
              <img
                src={p.src}
                alt={p.alt[lang]}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
            </motion.figure>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: EASE }}
          data-testid="gallery-quote-band"
          className="mt-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-gold/40 bg-navy px-6 py-7 sm:flex-row sm:px-10"
        >
          <div className="flex items-center gap-4">
            <img src="/logo-crest.png" alt="Equipment Concierge crest" className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/50" />
            <p className="font-serif text-xl italic text-cream sm:text-2xl">{t.gallery.quoteTitle}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="rounded-full border border-gold/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              {t.gallery.quote}
            </span>
            <a href={BRAND.phoneHref} data-testid="gallery-quote-call" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-gold-hover">
              <Phone className="h-3.5 w-3.5" />
              {BRAND.phone}
            </a>
          </div>
        </motion.div>

        <BrandsPanel />
      </div>

      <AnimatePresence>
        {open >= 0 && shown[open] && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={shown[open].alt[lang]}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/95 p-4 backdrop-blur-sm"
          >
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/30 text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/30 text-cream transition-colors duration-300 hover:border-gold hover:text-gold sm:left-6"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Next photo"
              className="absolute right-3 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/30 text-cream transition-colors duration-300 hover:border-gold hover:text-gold sm:right-6"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <motion.figure
              key={shown[open].key}
              data-testid="lightbox-figure"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="max-w-5xl"
            >
              <img
                src={shown[open].src}
                alt={shown[open].alt[lang]}
                className="max-h-[76vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 text-center">
                <span className="font-serif text-lg italic text-gold">
                  {GALLERY_GROUPS.find((g) => g.key === shown[open].group)?.label[lang]}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
